import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import { ChatResponse, ChatSuggestion } from './messagesSlice';
import { getSupabaseClient } from '@/utils/supabase';

// Define the backend connection slice
export interface BackendConnectionSlice {
	sendMessage: (message: string) => Promise<ChatResponse>;
	sendMessageStream: (
		message: string,
		handlers: {
			onMessage: (chunk: string) => void;
			onSuggestions: (s: ChatSuggestion[]) => void;
			onDone: () => void;
		}
	) => Promise<void>;
	getChatHistory: () => Promise<ChatResponse>;
	deleteChatThread: (threadId?: string) => Promise<void>;
	initializeChatBackend: (threadId: string) => Promise<ChatResponse>;
	invokeTool: (
		toolId: string,
		input?: Record<string, unknown>
	) => Promise<ChatResponse>;
	subscribeToNotifications: () => void;
	/** Clear the polling interval for notifications */
	unsubscribeFromNotifications: () => void;
	/** The agent type to request from the backend: 'execution' or 'conversion' */
	agentType: 'execution' | 'conversion';
	/** Change the agent type for subsequent requests */
	setAgentType: (type: 'execution' | 'conversion') => void;
}

const MASTRA_BACKEND_URL = 'https://modern-lemon-whale.mastra.cloud';
// const MASTRA_BACKEND_URL = 'http://localhost:4111';

export const createBackendConnectionSlice: StateCreator<
	CedarStore,
	[],
	[],
	BackendConnectionSlice
> = (set, get) => {
	// Polling interval handle
	let notificationInterval: number | undefined;

	return {
		// Agent type: 'execution' or 'conversion'
		agentType: 'execution',
		setAgentType: (type: 'execution' | 'conversion') =>
			set({ agentType: type }),
		// Initial state
		sendMessage: async (message: string): Promise<ChatResponse> => {
			// TODO: store user and conversation ids in supabase
			const userId = get().userId;
			const productId = get().productId;
			const threadId = get().currentThreadId;

			// Get user state context for the copilot if available
			const userStateContext = get().formatStateForCopilot();

			// Get available functions for the copilot if available
			const functionContext = get().formatStateFunctionsForCopilot();

			try {
				const response = await fetch(`${MASTRA_BACKEND_URL}/chat`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						message,
						userId,
						productId,
						threadId,
						agentRequested: get().agentType,
						userStateContext,
						functionContext,
					}),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				return data;
			} catch (error) {
				console.error('Error sending chat message:', error);
				throw error;
			}
		},

		sendMessageStream: async (
			message: string,
			handlers: {
				onMessage: (chunk: string) => void;
				onSuggestions: (s: ChatSuggestion[]) => void;
				onDone: () => void;
			}
		): Promise<void> => {
			const userId = get().userId;
			const productId = get().productId;
			const threadId = get().currentThreadId;

			const userStateContext = get().formatStateForCopilot();
			const functionContext = get().formatStateFunctionsForCopilot();

			try {
				const response = await fetch(`${MASTRA_BACKEND_URL}/chat/stream`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'text/event-stream',
					},
					body: JSON.stringify({
						message,
						userId,
						productId,
						threadId,
						agentRequested: get().agentType,
						userStateContext,
						functionContext,
					}),
				});

				if (!response.ok || !response.body) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = '';

				const parseEvent = (raw: string) => {
					let eventType = 'message';
					let data = '';
					for (const line of raw.split('\n')) {
						if (line.startsWith('event:')) {
							eventType = line.slice(6).trim();
						} else if (line.startsWith('data:')) {
							data +=
								eventType === 'suggestion'
									? line.slice(5) + ' '
									: line.slice(5);
						}
					}
					return { eventType, data };
				};

				while (true) {
					const { value, done } = await reader.read();
					if (done) break;
					buffer += decoder.decode(value, { stream: true });

					let idx;
					while ((idx = buffer.indexOf('\n\n')) !== -1) {
						const rawEvent = buffer.slice(0, idx);
						buffer = buffer.slice(idx + 2);
						if (!rawEvent.trim()) continue;
						const { eventType, data } = parseEvent(rawEvent);

						if (eventType === 'suggestions') {
							try {
								const json = JSON.parse(data) as ChatSuggestion[];
								handlers.onSuggestions(json);
							} catch (e) {
								console.error('Failed to parse suggestions', e);
							}
						} else if (eventType === 'done') {
							await handlers.onDone();
							return;
						} else {
							handlers.onMessage(data);
						}
					}
				}
			} catch (error) {
				console.error('Error streaming chat message:', error);
				throw error;
			}
		},

		initializeChatBackend: async (threadId: string): Promise<ChatResponse> => {
			const productId = get().productId;
			try {
				await get().validateOrCreateProductUser();
				const userId = get().userId;
				const response = await fetch(`${MASTRA_BACKEND_URL}/chat/initialize`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						userId,
						threadId,
						agentRequested: get().agentType,
						productId,
					}),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				await get().subscribeToNotifications();

				const data = await response.json();

				return data;
			} catch (error) {
				console.error('Error initializing chat:', error);
				throw error;
			}
		},

		invokeTool: async (
			toolId: string,
			input: Record<string, unknown> = {}
		): Promise<ChatResponse> => {
			const userId = get().userId;
			const productId = get().productId;
			const threadId = get().currentThreadId;
			try {
				const response = await fetch(`${MASTRA_BACKEND_URL}/chat/tool`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ productId, userId, threadId, toolId, input }),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				return data;
			} catch (error) {
				console.error('Error invoking tool:', error);
				throw error;
			}
		},

		getChatHistory: async (): Promise<ChatResponse> => {
			const userId = get().userId;
			const threadId = get().currentThreadId;
			try {
				const response = await fetch(`${MASTRA_BACKEND_URL}/chat/history`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						userId,
						threadId,
						agentRequested: get().agentType,
					}),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				return data as ChatResponse;
			} catch (error) {
				console.error('Error getting chat history:', error);
				throw error;
			}
		},

		deleteChatThread: async (threadId): Promise<void> => {
			try {
				let threadIdToDelete = threadId;
				if (!threadIdToDelete) {
					const { currentThreadId } = get();
					if (!currentThreadId) {
						throw new Error('No thread ID');
					}
					threadIdToDelete = currentThreadId;
				}

				const response = await fetch(`${MASTRA_BACKEND_URL}/chat/thread`, {
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						threadId: threadIdToDelete,
					}),
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
			} catch (error) {
				console.error('Error deleting chat thread:', error);
				throw error;
			}
		},

		// Poll for mastra_messages notifications for the active thread
		subscribeToNotifications: () => {
			if (typeof window === 'undefined') return;
			const supabase = getSupabaseClient();
			const threadId = get().currentThreadId;
			if (!threadId) return;
			// Clear any existing interval
			if (notificationInterval) {
				clearInterval(notificationInterval);
			}
			notificationInterval = window.setInterval(async () => {
				// Fetch messages marked for notification
				const { data: messages, error } = await supabase
					.from('mastra_messages')
					.select('id, content')
					.eq('thread_id', threadId)
					.eq('notify', true);
				if (error) {
					console.error('Error polling notifications:', error);
					return;
				}
				if (messages && messages.length > 0) {
					for (const msg of messages) {
						// Mark as handled
						await supabase
							.from('mastra_messages')
							.update({ notify: false })
							.eq('id', msg.id);
						// Enqueue toast action
						get().addAction({
							type: 'TOAST',
							title: 'New Message',
							description: msg.content,
							variant: 'default',
							position: 'top',
							duration: 4000,
						});
						// Open chat UI and refresh
						get().setShowCedarChat(true);
						await get().initializeChat();
					}
				}
			}, 5000);
		},

		/** Clear the polling interval for notifications */
		unsubscribeFromNotifications: () => {
			if (notificationInterval) {
				clearInterval(notificationInterval);
				notificationInterval = undefined;
			}
		},
	};
};
